// Task 8 — katalog birim alanları (varsayılan birim + alternatifler) için saf seçici mantığı:
// aile başlıklı gruplar (varsayılanla aynı aile önce), yazarak süzme, katsayı / çevrilmez ilişkisi.
// UnitCombobox bunu render eder; DOM'suz test edilir.

import {
  KPI_UNIT_GROUPS,
  convertUnit,
  currencyBaseOf,
  currencyScaleUnits,
  isSelectableUnit,
  normalizeUnitKey,
  unitGroupKeyOf,
  unitConversionFactor,
} from "@mahir/shared";
import { KPI_UNIT_COPY } from "./kpi-unit-copy";
import { formatUnitFactor, type UnitOptionGroup } from "./kpi-unit-options";
import { foldSearch } from "./search-select";

const CB = KPI_UNIT_COPY.combobox;
const BASE_CURRENCIES: readonly string[] = ["TL", "USD", "EUR", "GBP"];

/**
 * Aile başlıklı seçenek grupları. `related` (varsayılan birim) varsa onun ailesi ilk sırada,
 * "<Aile> — varsayılanla aynı aile" başlığıyla; seçeneklerde `related`'a göre katsayı ipucu.
 * `query` boş değilse etiketi içeren seçenekler kalır, boş gruplar düşer. Birim tek grupta.
 */
export function unitPickerGroups(args: {
  related?: string | null;
  query?: string;
  exclude?: readonly string[];
}): UnitOptionGroup[] {
  const relatedRaw = args.related?.trim() ?? "";
  const related = relatedRaw === "" ? null : relatedRaw;
  const q = foldSearch(args.query ?? "");
  const excluded = new Set((args.exclude ?? []).map(normalizeUnitKey));
  if (related) excluded.add(normalizeUnitKey(related));
  const relatedKey = related ? unitGroupKeyOf(related) : null;
  const relatedCurrency = related ? currencyBaseOf(related) : null;

  const hint = (u: string): string | null => {
    if (!related) return null;
    const k = unitConversionFactor(related, u);
    return k !== null && k !== 1 ? formatUnitFactor(k) : null;
  };
  const mk = (id: string, label: string, units: readonly string[]): UnitOptionGroup | null => {
    const options = units
      .filter((u) => !excluded.has(normalizeUnitKey(u)))
      .filter((u) => q === "" || foldSearch(u).includes(q))
      .map((u) => ({ value: u, label: u, hint: hint(u) }));
    return options.length > 0 ? { key: "family", id, label, options } : null;
  };

  const currencyUnits = [
    ...BASE_CURRENCIES,
    ...(relatedCurrency ? currencyScaleUnits(related ?? "") : []),
  ];
  const sources: { id: string; label: string; units: readonly string[]; isRelated: boolean }[] = [
    ...KPI_UNIT_GROUPS.map((g) => ({
      id: g.key,
      label: g.label,
      units: g.units,
      isRelated: relatedKey === g.key,
    })),
    { id: "currency", label: CB.currencyGroup, units: currencyUnits, isRelated: !!relatedCurrency },
  ];
  sources.sort((a, b) => Number(b.isRelated) - Number(a.isRelated));

  const seen = new Set<string>();
  const out: UnitOptionGroup[] = [];
  for (const s of sources) {
    const units = s.units.filter((u) => {
      const k = normalizeUnitKey(u);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const g = mk(s.id, s.isRelated ? CB.sameFamily(s.label) : s.label, units);
    if (g) out.push(g);
  }
  return out;
}

export type UnitRelation =
  | { kind: "none" }
  | { kind: "factor"; factor: number; text: string }
  | { kind: "noConversion" }
  | { kind: "unknown" };

/** Alternatifin varsayılanla ilişkisi: katsayı ("1 GJ = 277,8 kWh") / çevrilmez / bilinmeyen. */
export function unitRelation(defaultUnit: string, alt: string): UnitRelation {
  // varsayılan boş / şablon ({kod}) / tanınmayan ise ilişki gösterilmez (`none`)
  if (!isSelectableUnit(alt)) return { kind: "unknown" };
  const def = defaultUnit.trim();
  if (def === "" || !isSelectableUnit(def)) return { kind: "none" };
  const f = convertUnit(1, def, alt);
  if (f === null) return { kind: "noConversion" };
  const factor = f.toLocaleString("tr-TR", { maximumSignificantDigits: 4 });
  return {
    kind: "factor",
    factor: f,
    text: KPI_UNIT_COPY.variantsField.relation(def, factor, alt),
  };
}
